<script setup>
// core
import {onUnmounted, ref, watch} from 'vue'
import {useStore} from "vuex";

// components
import PreloaderBase from "@/components/Recurring/PreloaderBase";
import HeaderMain from "@/components/Recurring/HeaderMain.vue";
import TimerBlock from "@/components/Recurring/TimerBlockRecurring";

const props = defineProps({
    qrLink: String
})
const emit = defineEmits(['nextStep'])

const store = useStore()
const qrSrc = ref(null)
const inputValue = ref("")

const timerFinish = () => {
    emit('nextStep', "timeOver")
}

const openModal = () => {
    store.commit('openDialog', {
        name: 'ActivationLinkDialog',
        link: inputValue.value,
    })
};

const qrURL = async (url) => {
    !url && emit("nextStep", "oops");

    let link = await url.replace(/&amp;/g, "&");
    link = encodeURIComponent(link);

    qrSrc.value = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${link}`;
    inputValue.value = url;
};

watch(() => props.qrLink, () => {
    qrURL(props.qrLink)
}, {immediate: true})

// the dialog lives in AppDialog and would outlive the expired link
onUnmounted(() => {
    if (store.state.componentDialog === 'ActivationLinkDialog' && store.state.isDialogOpen) {
        store.commit('closeDialog')
    }
})
</script>

<template>
    <div>
        <HeaderMain>{{ $t('telephony.activation.header') }}</HeaderMain>

        <div class="block">
            <Transition name="fade" mode="out-in">
                <div class="buttonCase" :style="{ paddingTop: !qrSrc ? '40px' : '' }">
                    <div class="qr_block">
                        <Transition name="fade" mode="out-in">
                            <img v-if="qrSrc" :src="qrSrc" alt="qr"/>
                            <PreloaderBase v-else/>
                        </Transition>
                    </div>
                    <Transition name="fade" mode="out-in">
                        <button
                            v-if="qrSrc"
                            type="button"
                            class="notWorkCam"
                            @click="openModal"
                        >
                            <i class="fa fa-camera"></i>
                            {{ $t(`page.generation.viber`) }}
                        </button>
                    </Transition>
                </div>
            </Transition>
            <div class="start">
                <p class="qr__timer-label">{{ $t('page.qr_timer.label') }}</p>

                <TimerBlock
                    class="timer"
                    :seconds="300"
                    @timerFinish="timerFinish"
                ></TimerBlock>

                <p class="qr__timer-note">{{ $t('page.qr_timer.note') }}</p>

                <ol class="qr__steps">
                    <li v-for="(step, i) in $tm('telephony.activation.scan_steps')" :key="i">
                        <span class="qr__num"></span>
                        <span>{{ step }}</span>
                    </li>
                </ol>

                <div class="qr__notice">
                    <i class="bi bi-exclamation-circle"></i>
                    <p>
                        <b>{{ $t('telephony.activation.important_label') }}</b>
                        {{ $t('telephony.activation.important_text') }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.buttonCase {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 300px;
    min-height: 300px;
}

.notWorkCam {
    // the caption hangs under the code instead of pulling it above the text
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    white-space: nowrap;
    margin-top: 0;
    color: var(--general-color);
    font-family: "Gilroy";
    font-style: normal;
    font-weight: 600;
    font-size: 15px;
    line-height: 17px;
    text-decoration: none;
    text-transform: uppercase;
    cursor: pointer;
    transition: 0.25s;
    padding: 13px 20px;
}


.block {
    box-sizing: border-box;
    width: 100%;
    background: var(--block-color);
    display: flex;
    align-items: center;
    border-radius: 11px;
    padding: 50px 35px 65px 35px;
    gap: 55px;

    .start {
        flex: 1;
        min-width: 0;

        p {
            max-width: 600px;
            width: 100%;
            display: flex;
            align-items: flex-start;
            gap: 10px;
            font-family: "Gilroy";
            font-style: normal;
            font-weight: 500;
            font-size: 22px;
            line-height: 30px;
            color: var(--text-second-color);
            margin: 10px 0 10px 0;
        }
    }
}

// the copy around the timer; .block .start p of this file is more specific
// than a two-class selector, hence the .block prefix
.block .start {
    .qr__timer-label {
        display: block;
        max-width: none;
        margin: 0 0 10px;
        font-family: "Gilroy", sans-serif;
        font-size: 16px;
        font-weight: 700;
        line-height: 25px;
        text-align: center;
        color: var(--text-color);
    }

    .qr__timer-note {
        display: block;
        max-width: none;
        margin: -18px 0 22px;
        font-family: "Gilroy", sans-serif;
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
        text-align: center;
        color: var(--text-third-color);
    }

    .qr__steps {
        list-style: none;
        counter-reset: qr-step;
        margin: 0 0 20px;
        padding: 0;

        display: flex;
        flex-direction: column;
        gap: 10px;

        li {
            counter-increment: qr-step;
            display: flex;
            align-items: flex-start;
            gap: 12px;
            margin: 0;

            font-family: "Gilroy", sans-serif;
            font-size: 16px;
            font-weight: 500;
            line-height: 25px;
            color: var(--text-third-color);
        }
    }

    .qr__num {
        flex: 0 0 auto;
        width: 26px;
        height: 26px;
        margin-top: 1px;

        display: inline-flex;
        align-items: center;
        justify-content: center;

        border-radius: 7px;
        background-color: #cdcaf3;
        color: var(--general-color);
        font-family: "Gilroy", sans-serif;
        font-size: 13px;
        font-weight: 700;

        &::before {
            content: counter(qr-step);
        }
    }

    .qr__notice {
        display: flex;
        align-items: flex-start;
        gap: 12px;

        padding: 14px 18px;
        border: 1px solid #cdcaf3;
        border-radius: 7px;
        background: #ffffff;
        box-sizing: border-box;

        i {
            flex: 0 0 auto;
            margin-top: 4px;
            font-size: 18px;
            line-height: 1;
            color: var(--general-color);
        }

        p {
            display: block;
            max-width: none;
            margin: 0;

            font-family: "Gilroy", sans-serif;
            font-size: 16px;
            font-weight: 500;
            line-height: 25px;
            color: var(--text-third-color);
        }

        b {
            font-weight: 700;
            color: var(--text-color);
        }
    }
}

.qr_block {
    position: relative;
    max-width: 300px;
    height: 300px;
}

@media screen and (max-width: 900px) {
    .qr_block {
        height: 200px;
        width: 200px;
    }

    .block {
        gap: 15px;

        .start {
        }
    }

    .buttonCase {
        min-width: 200px;
    }
}

@media screen and (max-width: 750px) {
    .qr_block {
        max-width: 300px;
        width: 100%;
        height: 300px;
    }

    .block {
        padding: 30px;
        gap: 30px;
        flex-direction: column;
        align-items: center;

        .start {
            .timer {
                margin: auto;
                margin-bottom: 30px;
            }

            p {
                font-size: 14px;
            }
        }
    }

}

@media screen and (max-width: 550px) {
    .start {
        // width: 90%;
    }

    .qr_block {
        height: 200px;
        width: 200px;
    }
}

@media screen and (max-width: 400px) {
    .block {
        padding: 10px;
    }

}
</style>
